import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { FaArrowLeft, FaSave } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/typeMaster";

const toInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const VehicleTypeMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ Accept multiple keys
  const vehicleTypeId = toInt(
    searchParams.get("vehicleTypeId") ||
      searchParams.get("id") ||
      searchParams.get("vehicletypeid")
  );

  const [loading, setLoading] = useState(false);
  const [vehicleTypeName, setVehicleTypeName] = useState("");

  const loadEdit = async () => {
    if (!vehicleTypeId) return;

    if (vehicleTypeId <= 0) {
      toast.error("Invalid vehicleTypeId in URL");
      return;
    }

    setLoading(true);
    try {
      try {
        const r1 = await axios.get(`${API_BASE}/${vehicleTypeId}`, {
          headers: commonHeaders,
        });

        if (r1.data?.success) {
          const name =
            r1.data?.data?.vehicleTypeName ??
            r1.data?.data?.fld_vehicletypename ??
            r1.data?.vehicleTypeName ??
            "";
          setVehicleTypeName(String(name || ""));
          return;
        }
      } catch (_) {}

      const r2 = await axios.get(`${API_BASE}/view`, {
        params: { vehicleTypeId },
        headers: commonHeaders,
      });

      if (!r2.data?.success) {
        toast.error(r2.data?.message || "Failed to load edit data");
        return;
      }

      const name =
        r2.data?.data?.vehicleTypeName ??
        r2.data?.data?.fld_vehicletypename ??
        r2.data?.vehicleTypeName ??
        "";
      setVehicleTypeName(String(name || ""));
    } catch (err) {
      console.error(err);
      toast.error("Server error while loading edit data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleTypeId, commonHeaders]);

  const handleSave = async () => {
    if (!vehicleTypeName.trim()) {
      toast.error("Please enter vehicle type!");
      return;
    }

    setLoading(true);
    try {
      const r = await axios.post(
        `${API_BASE}/save`,
        {
          vehicleTypeId: vehicleTypeId || 0,
          vehicleTypeName: vehicleTypeName.trim(),
        },
        {
          headers: commonHeaders,
        }
      );

      if (!r.data?.success) {
        toast.error(r.data?.message || "Save failed");
        return;
      }

      toast.success(r.data?.message || "Saved successfully!");
      navigate("/Vehicle/VehicleTypeMasterListing");
    } catch (err) {
      console.error(err);
      toast.error("Server error while saving");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE TYPE MASTER</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-6">
          <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
            {vehicleTypeId ? "Edit Vehicle Type" : "Add Vehicle Type"}
          </h1>

          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? "border-gray-600 text-white hover:bg-gray-800"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        <div
          className={`rounded-xl p-6 border ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="max-w-2xl mx-auto">
            <div className="mb-8">
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDark ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Vehicle Type
              </label>
              <input
                type="text"
                value={vehicleTypeName}
                onChange={(e) => setVehicleTypeName(e.target.value)}
                placeholder={loading ? "Loading..." : "Enter vehicle type..."}
                disabled={loading}
                className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                }`}
              />
            </div>

            <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setVehicleTypeName("")}
                className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Clear
              </button>

              <button
                onClick={handleSave}
                disabled={loading || !vehicleTypeName.trim()}
                className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loading || !vehicleTypeName.trim()
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaSave className="w-4 h-4" />
                {vehicleTypeId ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTypeMaster;